import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { Box, Flex, Tooltip } from '@chakra-ui/react';
import { Button, SectionLabel } from '@kula-ai/components';
import { Eye } from '@kula-ai/icons/outline/Eye';
import { Trash01 } from '@kula-ai/icons/outline/Trash01';
import { PlusCircle } from '@kula-ai/icons/outline/PlusCircle';
import { KSelect } from '@/ui-library/components/molecules/KSelect';
import moment from 'moment-timezone';
import { useDateFormat } from '@/hooks';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { formatTimezoneAbbreviationForDisplay } from '@/utils/formatTimezoneAbbreviation';
import {
  useCoordinatorTemplates,
  useCoordinatorJobConfig,
} from '@/api/ai-coordinator';
import { SCHEDULE_AI_COPY } from './constants';
import {
  formatCustomTimingInstantForDropdown,
  getScheduleTimingSelectOptions,
  hasValidCandidateTimezone,
  isCustomScheduleInFuture,
} from './scheduleWithAiTimezoneUtils';
import {
  TemplatePreviewModal,
  CustomDateModal,
  TimingSection,
} from './ScheduleWithAiDrawer/components';
import { computeScheduledAtISO } from './ScheduleWithAiDrawer/hooks/scheduleWithAiForm.pure';

const COPY = SCHEDULE_AI_COPY;

interface CoordinatorScheduleStepProps {
  applicationData?: {
    contact?: { time_zone?: string | null } | null;
    ats_job?: { id?: number | null } | null;
    job_id?: number | null;
  } | null;
  onTimingChange?: (_timing: string) => void;
  onValidityChange?: (_isValid: boolean) => void;
}

export const CoordinatorScheduleStep = forwardRef(
  (
    {
      applicationData,
      onTimingChange,
      onValidityChange,
    }: CoordinatorScheduleStepProps,
    ref
  ) => {
    const [timing, setTiming] = useState('immediately');
    const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [customDateModalOpen, setCustomDateModalOpen] = useState(false);
    const [customSchedule, setCustomSchedule] = useState(null);
    const [minuteTick, setMinuteTick] = useState(0);

    const currUser = useSelector(selectCurrUser);
    const { dateFormat, timeFormat } = useDateFormat();
    const userTimezoneKey = currUser?.time_zone || moment.tz.guess();
    const { contact, ats_job } = applicationData || {};
    const jobId = ats_job?.id ?? applicationData?.job_id ?? null;
    const hasCandidateTimezone = hasValidCandidateTimezone(contact);
    const candidateTimezoneKey = hasCandidateTimezone
      ? String(contact.time_zone)
      : userTimezoneKey;
    const mCandidateNow = moment().tz(candidateTimezoneKey);
    const candidateCurrentTime = `${mCandidateNow.format('h:mm A')} ${formatTimezoneAbbreviationForDisplay(mCandidateNow, candidateTimezoneKey)}`;

    useEffect(() => {
      const id = setInterval(() => setMinuteTick((n) => n + 1), 60000);
      return () => clearInterval(id);
    }, []);

    const scheduleTimingOptions = useMemo(() => {
      void minuteTick;
      return getScheduleTimingSelectOptions({
        hasCandidateTimezone,
        displayTimezoneKey: candidateTimezoneKey,
      });
    }, [hasCandidateTimezone, candidateTimezoneKey, minuteTick]);

    useEffect(() => {
      if (!hasCandidateTimezone && timing === 'custom') {
        const id = setTimeout(() => {
          setTiming('immediately');
          setCustomSchedule(null);
        }, 0);
        return () => clearTimeout(id);
      }
      return undefined;
    }, [hasCandidateTimezone, timing]);

    useEffect(() => {
      if (timing !== 'today_evening') return undefined;
      const sixPm = moment()
        .tz(candidateTimezoneKey)
        .startOf('day')
        .add(18, 'hours');
      if (!sixPm.isAfter(moment())) {
        const id = setTimeout(() => setTiming('immediately'), 0);
        return () => clearTimeout(id);
      }
      return undefined;
    }, [timing, candidateTimezoneKey, minuteTick]);

    const candidateCurrentTimeLine = useMemo(() => {
      if (!hasCandidateTimezone) return null;
      return `${COPY.currentTimeForCandidate}: ${candidateCurrentTime}`;
    }, [hasCandidateTimezone, candidateCurrentTime]);

    const customSchedulePastError =
      timing === 'custom' &&
      Boolean(
        customSchedule?.date &&
        customSchedule?.time &&
        customSchedule?.timezoneKey
      ) &&
      !isCustomScheduleInFuture(customSchedule);

    const scheduledAtISO = useMemo(() => {
      void minuteTick;
      return computeScheduledAtISO({
        timing,
        customSchedule,
        candidateTimezoneKey,
      });
    }, [timing, customSchedule, candidateTimezoneKey, minuteTick]);

    const scheduleDisplayInCandidateTimezone = useMemo(() => {
      if (timing === 'immediately') return null;
      if (!scheduledAtISO) return null;
      const m = moment.utc(scheduledAtISO).tz(candidateTimezoneKey);
      return formatCustomTimingInstantForDropdown(m, candidateTimezoneKey, {
        dateMomentFormat: dateFormat.label,
        timeMomentFormat: timeFormat,
      });
    }, [
      timing,
      scheduledAtISO,
      candidateTimezoneKey,
      dateFormat.label,
      timeFormat,
    ]);

    const { data: jobConfig } = useCoordinatorJobConfig(
      jobId !== null ? String(jobId) : null,
      true
    );
    const { data: publishedTemplates = [] } =
      useCoordinatorTemplates('published');

    const jobTemplates = useMemo(
      () => jobConfig?.coordinator_templates ?? [],
      [jobConfig?.coordinator_templates]
    );

    const templates = useMemo(() => {
      const byId = new Map();
      publishedTemplates.forEach((t) => byId.set(t.id, t));
      jobTemplates.forEach((t) => byId.set(t.id, t));
      return Array.from(byId.values());
    }, [publishedTemplates, jobTemplates]);

    const templateOptions = useMemo(
      () => templates?.map((t) => ({ label: t?.name, value: t?.id })) ?? [],
      [templates]
    );

    useEffect(() => {
      if (!Array.isArray(jobTemplates) || jobTemplates.length === 0) return;
      const id = setTimeout(() => {
        setSelectedTemplateIds((prev) =>
          prev.length === 0 ? jobTemplates.map((t) => t.id) : prev
        );
      }, 0);
      return () => clearTimeout(id);
    }, [jobTemplates]);

    const selectedValues = useMemo(
      () => new Set(selectedTemplateIds),
      [selectedTemplateIds]
    );
    const getAvailableOptions = useCallback(
      (currentValue) =>
        templateOptions.filter(
          (opt) => !selectedValues.has(opt.value) || opt.value === currentValue
        ),
      [templateOptions, selectedValues]
    );

    const allTemplatesSelected =
      templateOptions.length > 0 &&
      selectedTemplateIds.length >= templateOptions.length;
    const hasUnselectedRow = selectedTemplateIds.some((id) => !id);
    const addDisabled =
      templateOptions.length === 0 || allTemplatesSelected || hasUnselectedRow;
    const addTooltip =
      templateOptions.length === 0
        ? 'No templates available'
        : allTemplatesSelected
          ? 'All templates selected'
          : hasUnselectedRow
            ? 'Select a template first'
            : '';

    const hasCustomFields =
      Boolean(customSchedule?.date) &&
      Boolean(customSchedule?.time) &&
      Boolean(customSchedule?.timezoneKey);
    const customTimingValid =
      timing !== 'custom' ||
      (hasCandidateTimezone &&
        hasCustomFields &&
        isCustomScheduleInFuture(customSchedule));

    useImperativeHandle(
      ref,
      () => ({
        getModel: () => {
          return Promise.resolve({
            hasValidData: customTimingValid,
            payload: {
              coordinator_template_id: selectedTemplateIds[0] || null,
              timing,
              customSchedule,
            },
          });
        },
      }),
      [selectedTemplateIds, timing, customSchedule, customTimingValid]
    );

    useEffect(() => {
      onTimingChange?.(timing);
    }, [timing, onTimingChange]);

    useEffect(() => {
      onValidityChange?.(customTimingValid);
    }, [customTimingValid, onValidityChange]);

    const timingSectionFocusRef = useRef(null);

    return (
      <Box p="16px 24px" overflowY="auto" flex={1}>
        <Box ref={timingSectionFocusRef} tabIndex={-1} outline="none">
          <TimingSection
            timing={timing}
            setTiming={setTiming}
            setCustomSchedule={setCustomSchedule}
            scheduleDisplayInCandidateTimezone={
              scheduleDisplayInCandidateTimezone
            }
            candidateCurrentTimeLine={candidateCurrentTimeLine}
            hasCandidateTimezone={hasCandidateTimezone}
            missingTimezoneMessage={COPY.candidateTimezoneMissing}
            customSchedulePastError={customSchedulePastError}
            timingOptions={scheduleTimingOptions}
            onOpenCustomDateModal={() => setCustomDateModalOpen(true)}
          />
        </Box>

        <Box mb="20px">
          <SectionLabel>{COPY.contextTemplateLabel}</SectionLabel>

          {selectedTemplateIds.length > 0 && (
            <Flex flexDir="column" gap="8px" mb="10px" mt="12px">
              {selectedTemplateIds.map((val, idx) => {
                const templateObj = templates.find((t) => t.id === val);
                return (
                  <Flex key={idx} gap="8px" alignItems="center">
                    <Box flex="1">
                      <KSelect
                        options={getAvailableOptions(val)}
                        value={
                          templateOptions.find((opt) => opt.value === val) ||
                          null
                        }
                        onChange={(opt) => {
                          const updated = [...selectedTemplateIds];
                          updated[idx] = opt?.value || '';
                          setSelectedTemplateIds(updated);
                        }}
                        placeholder="Select context template"
                      />
                    </Box>
                    <Button
                      type="button"
                      variant="quaternary"
                      size="icon-large"
                      disabled={!val}
                      onClick={() => setPreviewTemplate(templateObj)}
                      leftElement={
                        <Eye width={20} height={20} color="#6D659D" />
                      }
                    />
                    <Button
                      type="button"
                      variant="quaternary"
                      size="icon-large"
                      onClick={() =>
                        setSelectedTemplateIds(
                          selectedTemplateIds.filter((_, i) => i !== idx)
                        )
                      }
                      leftElement={
                        <Trash01 width={20} height={20} color="#6D659D" />
                      }
                    />
                  </Flex>
                );
              })}
            </Flex>
          )}

          <Tooltip label={addTooltip} isDisabled={!addDisabled} hasArrow>
            <Box w="fit-content" mt="12px">
              <Button
                type="button"
                variant="quaternary"
                size="sm"
                disabled={addDisabled}
                onClick={() =>
                  setSelectedTemplateIds([...selectedTemplateIds, ''])
                }
                leftElement={<PlusCircle width={16} height={16} />}
              >
                Add another template
              </Button>
            </Box>
          </Tooltip>
        </Box>

        {previewTemplate && (
          <TemplatePreviewModal
            template={previewTemplate}
            templateId={previewTemplate.id}
            name={previewTemplate.name}
            isOpen={!!previewTemplate}
            onClose={() => setPreviewTemplate(null)}
          />
        )}

        <CustomDateModal
          isOpen={customDateModalOpen}
          onClose={() => setCustomDateModalOpen(false)}
          initialValue={customSchedule}
          onConfirm={(val) => setCustomSchedule(val)}
          defaultTimezoneKey={candidateTimezoneKey}
          finalFocusRef={timingSectionFocusRef}
        />
      </Box>
    );
  }
);

CoordinatorScheduleStep.displayName = 'CoordinatorScheduleStep';
